"use server";
/**
 * 輸入測試通行帳密（/access）：比對 ACCOUNT＋PASSWORD，對了就發簽章 cookie（30 天）並回原頁。
 * 防猜：同一個來源 IP 10 分鐘內最多試 10 次（記在記憶體，重啟清空）。
 */
import { createHash, timingSafeEqual } from "node:crypto";
import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { ACCESS_COOKIE, accessToken } from "@/lib/access";

export interface AccessState { error: string | null }

const WINDOW_MS = 10 * 60_000;
const MAX_TRIES = 10;
const g = globalThis as unknown as { __accessTries?: Map<string, number[]> };
g.__accessTries ??= new Map();

/** 來源 IP：經 Cloudflare 用 cf-connecting-ip，否則 x-forwarded-for 第一個 */
async function clientIp() {
  const h = await headers();
  return h.get("cf-connecting-ip") ?? h.get("x-forwarded-for")?.split(",")[0].trim() ?? "local";
}

/** 兩個字串比對（先雜湊成同長度，避免時間差洩漏） */
const same = (a: string, b: string) =>
  timingSafeEqual(createHash("sha256").update(a).digest(), createHash("sha256").update(b).digest());

export async function accessAction(_prev: AccessState, form: FormData): Promise<AccessState> {
  const token = accessToken();
  const next = String(form.get("next") ?? "");
  const target = next.startsWith("/") && !next.startsWith("//") && next !== "/access" ? next : "/watchdog/zwei";
  if (!token) redirect(target);

  const ip = await clientIp();
  const now = Date.now();
  const tries = (g.__accessTries!.get(ip) ?? []).filter((t) => now - t < WINDOW_MS);
  if (tries.length >= MAX_TRIES) return { error: "嘗試太多次，請 10 分鐘後再試" };

  // 帳號、密碼兩個都比（不短路，兩個都算完才判斷）
  const okUser = same(String(form.get("user") ?? "").trim(), process.env.ACCOUNT ?? "");
  const okCode = same(String(form.get("code") ?? ""), process.env.PASSWORD!);
  if (!(okUser && okCode)) {
    tries.push(now);
    g.__accessTries!.set(ip, tries);
    return { error: "帳號或密碼不對" };
  }
  g.__accessTries!.delete(ip);

  const https = (await headers()).get("x-forwarded-proto") === "https";
  (await cookies()).set(ACCESS_COOKIE, token, { httpOnly: true, sameSite: "lax", secure: https, path: "/", maxAge: 30 * 86_400 });
  redirect(target);
}
